import {
  ActionWithPayload,
  BaseFilter,
  CountiesFilter,
  PaginatedResponse,
  ResponseError,
  WithCallback,
} from "@shared/interfaces";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { showNotification, startLoading, stopLoading } from "@shared/store/actions";
import { Account, County, State, User } from "@shared/models";
import { actions as accountActions, selectors as accountSelectors } from "@containers/Account/store";
import {
  activateClient,
  changeClientAccountStatus,
  createClient,
  deleteClient,
  getAvailableFeatures,
  getClient,
  getClientList,
  getCounties,
  getStates,
  overrideFeature,
  resetFeature,
  updateClient,
} from "@containers/Contact/store/actions";
import { AvailableFeature, CreateClientDto, UpdateClientDto } from "@containers/Contact/interfaces";
import { showHttpErrorNotification } from "@shared/utils";
import { availableFeatures } from "@containers/Contact/store/selectors";

import api from "../api";

function* getClientListSaga({ payload }: ActionWithPayload<BaseFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<User> = yield call(api.getClientsList, payload);
    yield put(
      getClientList.success({
        ...response,
        clear: payload.page === 1,
      }),
    );
  } catch (error) {
    yield put(getClientList.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getClientSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: User = yield call(api.getClient, payload);
    yield put(getClient.success(response));
  } catch (error) {
    yield put(getClient.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* createClientSaga({ payload }: ActionWithPayload<WithCallback<CreateClientDto>>) {
  const { callback, ...rest } = payload;
  try {
    yield put(startLoading());
    const response: User = yield call(api.createClient, rest);
    yield put(createClient.success(response));
    yield put(
      showNotification({
        message: "The client was successfully created.",
        appearance: "success",
      }),
    );
    if (callback) {
      callback();
    }
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError, "The client was not created. Please try again."));
    yield put(createClient.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* updateClientSaga({ payload }: ActionWithPayload<WithCallback<UpdateClientDto & { id: number }>>) {
  const { callback, ...rest } = payload;
  try {
    yield put(startLoading());
    const response: User = yield call(api.updateClient, rest);
    yield put(updateClient.success(response));
    yield put(
      showNotification({
        message: "The client was successfully updated.",
        appearance: "success",
      }),
    );
    if (callback) {
      callback();
    }
  } catch (error) {
    yield put(
      showHttpErrorNotification(
        error as ResponseError,
        (error as ResponseError)?.message || "The client was not updated. Please try again.",
      ),
    );
    yield put(updateClient.failure(error as Error));
  } finally {
    yield put(stopLoading());
  }
}

function* deleteClientSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: User = yield call(api.deleteClient, payload);
    yield put(deleteClient.success(response));
  } catch (error) {
    yield put(deleteClient.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* activateClientSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: User = yield call(api.activateClient, payload);
    yield put(activateClient.success(response));
  } catch (error) {
    yield put(activateClient.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getStatesSaga({ payload }: ActionWithPayload<BaseFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<State> = yield call(api.getStates, payload);
    yield put(getStates.success(response));
  } catch (error) {
    yield put(getStates.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getCountiesSaga({ payload }: ActionWithPayload<CountiesFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<County> = yield call(api.getCounties, payload);
    yield put(getCounties.success(response));
  } catch (error) {
    yield put(getCounties.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* changeClientAccountStatusSaga({
  payload,
}: ActionWithPayload<{ user_id: number; account_id: number; is_active: boolean; callback: () => void }>) {
  try {
    yield put(startLoading());
    const response: User = yield call(api.changeClientAccountStatus, payload);
    yield put(getClient.success(response));

    if (payload.callback) {
      payload.callback();
    }
  } catch (error) {
    yield put(getClient.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getAvailableFeaturesSaga({ payload }: ReturnType<typeof getAvailableFeatures.request>) {
  try {
    yield put(startLoading());
    const features: AvailableFeature[] = yield call(api.getAvailableFeatures, payload);

    yield put(getAvailableFeatures.success(features));
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* overrideFeatureSaga({ payload }: ReturnType<typeof overrideFeature.request>) {
  try {
    yield put(startLoading());
    yield call(api.overrideFeature, payload);
    yield put(overrideFeature.success(payload));
    const features: AvailableFeature[] = yield select(availableFeatures);
    const feature = features.find((f) => f.id === payload.feature_id);

    if (feature) {
      yield put(
        showNotification({
          appearance: "success",
          message: `«${feature.description}» feature was ${payload.enabled ? "enabled" : "disabled"}`,
        }),
      );
    }
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* resetFeatureSaga({ payload }: ReturnType<typeof resetFeature.request>) {
  try {
    yield put(startLoading());
    yield call(api.resetFeature, payload);
    yield put(resetFeature.success(payload));
    const features: AvailableFeature[] = yield select(availableFeatures);
    const feature = features.find((f) => f.id === payload.feature_id);

    if (feature) {
      yield put(
        showNotification({
          appearance: "success",
          message: `«${feature.description}» feature was reset to defaults`,
        }),
      );
    }
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* setActiveAccountFeaturesSaga({ payload }: ActionWithPayload<{ account_id: number }>) {
  const activeAccount: Account = yield select(accountSelectors.getActiveAccount());
  if (payload.account_id === activeAccount.id) {
    const allFeatures: AvailableFeature[] = yield select(availableFeatures);
    const enabled = allFeatures.filter((f) => f.override?.enabled ?? f.enabled).map((f) => f.id);

    yield put(accountActions.setFeatures(enabled));
  }
}

function* clientSaga() {
  yield takeLatest(getClientList.request, getClientListSaga);
  yield takeLatest(getClient.request, getClientSaga);
  yield takeLatest(createClient.request, createClientSaga);
  yield takeLatest(updateClient.request, updateClientSaga);
  yield takeLatest(deleteClient.request, deleteClientSaga);
  yield takeLatest(activateClient.request, activateClientSaga);
  yield takeLatest(getStates.request, getStatesSaga);
  yield takeLatest(getCounties.request, getCountiesSaga);
  yield takeLatest(changeClientAccountStatus.request, changeClientAccountStatusSaga);
  yield takeLatest(getAvailableFeatures.request, getAvailableFeaturesSaga);
  yield takeLatest(overrideFeature.request, overrideFeatureSaga);
  yield takeLatest(resetFeature.request, resetFeatureSaga);
  yield takeEvery(overrideFeature.success, setActiveAccountFeaturesSaga);
  yield takeEvery(resetFeature.success, setActiveAccountFeaturesSaga);
}

export default clientSaga;
